import "./index.scss";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import AppShell from "../../components/appShell";
import Modal from "../../components/modal";
import TeamCards from "../../components/teamCards";
import { Avatar, money } from "../../components/atoms";
import { useAuctionRules } from "../../config/useAuctionMode";
import {
  boughtPlayers,
  selectBoughtPlayerData,
  selectFilteredPlayers,
  purseremaining as getPurse,
  selectisLoggedIn,
  selectTeamName,
  selectpurseremaining,
  activeBidding,
  blockStatus,
  selectactiveBidding,
  selectBidderProfile,
  getActiveSession as getBidderSession,
} from "../../store/features/bidderSlice";
import {
  getActiveSession,
  selectActiveSession,
} from "../../store/features/curatorSlice";
import { useSocketRoom } from "../../hooks/useSocketRoom";

const ViewAllPlayers = ({ socket }) => {
  let [showLoading, setShowLoading] = useState(false);
  let [counter, setCounter] = useState(1);
  let [content, setContent] = useState("");
  let isLoggedIn = useSelector(selectisLoggedIn);
  let playersData = useSelector(selectBoughtPlayerData);
  let filteredPlayerData = useSelector(selectFilteredPlayers);
  let profile = useSelector(selectBidderProfile);
  let teamName = useSelector(selectTeamName);
  let teamColor = playersData?.Franchise?.color || { hex: "#0040A8" };
  let secondaryTeamColor = playersData?.Franchise?.secondaryColor || {
    hex: "red",
  };

  let purseLeft = useSelector(selectpurseremaining);
  let session = useSelector(selectActiveSession);
  let activePlayer = useSelector(selectactiveBidding);
  let auctionRules = useAuctionRules();
  let dispatch = useDispatch();

  // Rejoins on every reconnect, so a dropped socket recovers on its own.
  useSocketRoom(socket, { role: "bidder", id: profile?._id, sessionId: session?.sessionId });

  // The old card filled itself with the team's colour, so it had to work out
  // whether to print on it in black or white. The card is on the system's tint
  // now and the team colour is only a small marker, so that sum is gone.

  useEffect(() => {
    dispatch(boughtPlayers());
    dispatch(activeBidding());
    dispatch(getPurse());
    dispatch(boughtPlayers());
    dispatch(getActiveSession());
    // A bidder opening this page directly has no curator session, so fetch
    // their own too - it carries the squad rules this screen displays.
    dispatch(getBidderSession());
    dispatch(blockStatus());
  }, [counter]);

  useEffect(() => {
    socket.on("block-started", (playerType) => {
      setContent(playerType?.toUpperCase() + " BLOCK STARTED!");
      setShowLoading(true);
      setCounter((a) => a + 1);
      setTimeout(() => {
        setShowLoading(false);
      }, 2500);
    });

    socket.on("block-paused", () => {
      setContent("BIDDING STOPPED!");
      setShowLoading(true);
      setCounter((a) => a + 1);
      setTimeout(() => {
        setShowLoading(false);
      }, 2500);
    });

    socket.on("block-finished", (playerType) => {
      setShowLoading(true);
      // console.log(playerType);
      setContent(playerType?.toUpperCase() + " BLOCK FINISHED!");
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    });

    socket.on("bid-updated", () => {
      setCounter((a) => a + 1);
    });

    socket.on("rtm-updated", () => {
      setCounter((a) => a + 1);
    });

    socket.on("set-player-unsold", () => {
      setContent(
        <div className="call">
          <span className="call-verdict danger">Unsold</span>
        </div>
      );
      setShowLoading(true);
      setTimeout(() => {
        window.location.reload();
      }, 3000);
      setCounter((a) => a + 1);
    });

    socket.on("set-player-sold", () => {
      // console.log(activePlayer);
      setShowLoading(true);
      setContent(
        <div className="call">
          <span className="call-verdict success">Sold</span>
          <span className="call-team">{activePlayer?.lastBidBy?.team}</span>
        </div>
      );
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    });
  }, []);


  if (!isLoggedIn) {
    return (
      <AppShell role="public">
        <h1 className="au-heading">Not signed in</h1>
        <p className="au-subheading">
          Open the link you were sent, or{" "}
          <Link to="/bidder/login">sign in as a bidder</Link>.
        </p>
      </AppShell>
    );
  }

  // Minimums and maximums come from the rules the auction started under, so a
  // women's auction shows its Indian-player floor and a men's does not.
  const limit = (used, rule) => {
    if (!rule) return String(used);
    if (rule.max) return `${used} / ${rule.max}`;
    if (rule.min) return `${used} · min ${rule.min}`;
    return String(used);
  };

  const criteria = [
    ["Overseas", limit(filteredPlayerData?.Overseas?.length || 0, auctionRules?.overseas)],
    ["Indians", limit(filteredPlayerData?.Indians?.length || 0, auctionRules?.indians)],
    ["Uncapped", String(filteredPlayerData?.Uncapped?.length || 0)],
    ["Batters", String(filteredPlayerData?.Batter?.length || 0)],
    ["Bowlers", String(filteredPlayerData?.Bowler?.length || 0)],
    ["Wicketkeepers", String(filteredPlayerData?.Wicketkeeper?.length || 0)],
    ["All rounders", String(filteredPlayerData?.["All Rounder"]?.length || 0)],
  ];

  const squadSize = ["Batter", "Bowler", "Wicketkeeper", "All Rounder"].reduce(
    (n, k) => n + (filteredPlayerData?.[k]?.length || 0),
    0
  );

  return (
    <AppShell role="bidder">
      <Modal show={showLoading} content={content} />

      <header className="squad-head">
        <div className="squad-head-who">
          <Avatar
            name={teamName}
            src={playersData?.Franchise?.image}
            size={44}
            logo
          />
          <div>
            <h1 className="au-heading">{teamName || "My Squad"}</h1>
            <p className="au-subheading">
              {squadSize} {squadSize === 1 ? "player" : "players"} bought
              {auctionRules?.squad?.max ? ` of ${auctionRules.squad.max}` : ""}.
            </p>
          </div>
        </div>

        <div className="squad-purse">
          <span className="au-label">Purse left</span>
          <span className="squad-purse-value">{money(purseLeft?.value)}</span>
        </div>
      </header>

      <div className="squad-layout">
        <section className="au-card squad-criteria">
          <span className="au-label">Criteria</span>
          <ul className="squad-criteria-list">
            {criteria.map(([label, value]) => (
              <li key={label} className="squad-criteria-row">
                <span className="squad-criteria-label">{label}</span>
                <span className="squad-criteria-value">{value}</span>
              </li>
            ))}
          </ul>
        </section>

        <TeamCards
          headless
          team={playersData?.Franchise}
          teamColor={teamColor?.hex}
          secondaryTeamColor={secondaryTeamColor?.hex}
          filteredPlayerData={filteredPlayerData}
          teamName={teamName}
        />
      </div>
    </AppShell>
  );
};

export default ViewAllPlayers;
